import AddressAutocompleteInput from "@/components/AddressAutoComplete";
import Autocomplete from "@/components/Autocomplete";
import TextField from "@/components/TextField";
import PostalCodeInput from "@/components/AddressAutoComplete/PostalCodeInput";
import StateInput from "@/components/AddressAutoComplete/StateInput";
import { COUNTRY_CODES, COUNTRY_CODE_OPTIONS } from "@/lib/countryHelpers";
import { useEffect, useRef, useState } from "react";
import { Controller, type FieldError, type FieldErrors, useFormContext } from "react-hook-form";

function getAddressFieldError(errors: FieldErrors, addressName: string, field: string): FieldError | undefined {
  const addressErrors = errors[addressName as keyof FieldErrors] as Record<string, FieldError | undefined> | undefined;
  return addressErrors?.[field];
}

const AddressInput = ({
  name,
  variant = "outlined",
  label,
  boldLabel = false,
  required = false
}: {
  name: string;
  variant?: "outlined" | "filled";
  label?: string;
  boldLabel?: boolean;
  required?: boolean;
}) => {
  const {
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting }
  } = useFormContext();
  const countryCode = watch(`${name}.countryCode`);
  const isUSA = countryCode === "USA";
  const previousCountryRef = useRef<string>("");

  const [countryQuery, setCountryQuery] = useState(COUNTRY_CODES[watch(`${name}.countryCode`)] || "");
  const filteredCountryOptions = COUNTRY_CODE_OPTIONS.filter(
    (c) =>
      c.label.toLowerCase().includes(countryQuery.toLowerCase()) ||
      c.value.toLowerCase().includes(countryQuery.toLowerCase())
  );
  const setAddressField = (field: string, value: string) =>
    setValue(`${name}.${field}`, value, { shouldDirty: true, shouldValidate: true });

  useEffect(() => {
    if (previousCountryRef.current && previousCountryRef.current !== countryCode) {
      setValue(`${name}.address1`, "", { shouldDirty: true, shouldValidate: true });
      setValue(`${name}.address2`, "", { shouldDirty: true, shouldValidate: true });
      setValue(`${name}.city`, "", { shouldDirty: true, shouldValidate: true });
      setValue(`${name}.state`, "", { shouldDirty: true, shouldValidate: true });
      setValue(`${name}.postalCode`, "", { shouldDirty: true, shouldValidate: true });
    }
    previousCountryRef.current = countryCode || "";
  }, [countryCode, name, setValue]);

  useEffect(() => {
    if (!countryCode) return;

    const countryLabel = COUNTRY_CODES[countryCode];
    if (countryLabel) {
      setCountryQuery(countryLabel);
    }
  }, [countryCode]);

  return (
    <div className="flex flex-col">
      {label && (
        <div
          className={
            boldLabel
              ? "mb-3 text-label-medium font-semibold text-sys-brand-on-surface"
              : "text-label-medium text-sys-brand-on-surface-variant my-4"
          }
        >
          {label}
        </div>
      )}
      <div className="flex flex-col gap-6">
        <div className="flex">
          <div className="flex-1">
            <Controller
              name={`${name}.countryCode`}
              control={control}
              render={({ field: { onChange }, fieldState }) => (
                <Autocomplete<{ value: string; label: string }>
                  variant="searchableDropdown"
                  data={filteredCountryOptions}
                  query={countryQuery}
                  label="Country"
                  renderItem={(opt) => <>{opt.label}</>}
                  reset={() => {
                    onChange("");
                    setCountryQuery("");
                    setAddressField("address1", "");
                    setAddressField("address2", "");
                    setAddressField("city", "");
                    setAddressField("state", "");
                    setAddressField("postalCode", "");
                  }}
                  onChangeText={setCountryQuery}
                  onSelect={({ value, label }) => {
                    onChange(value);
                    setCountryQuery(label);
                  }}
                  disabled={isSubmitting}
                  error={!!fieldState.error}
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <AddressAutocompleteInput
              name={`${name}.address1`}
              control={control}
              country={countryCode}
              required={required}
              onSuggest={(a) => {
                const resolvedCountryCode = countryCode || "USA";
                if (!countryCode) {
                  setValue(`${name}.countryCode`, resolvedCountryCode, { shouldDirty: true, shouldValidate: true });
                  setCountryQuery(COUNTRY_CODES[resolvedCountryCode] || "");
                }
                setAddressField("address1", a.address1);
                setAddressField("address2", a.address2);
                setAddressField("city", a.city);
                setAddressField("state", a.state);
                setAddressField("postalCode", a.postalCode);
              }}
              label="Address 1"
            />
          </div>
          <TextField
            className="flex-1"
            name={`${name}.address2`}
            label="Address 2"
            variant={variant}
            value={watch(`${name}.address2`)}
            onChangeText={(value) => setAddressField("address2", value)}
            disabled={isSubmitting}
            error={!!getAddressFieldError(errors, name, "address2")}
            errorText={getAddressFieldError(errors, name, "address2")?.message}
          />
        </div>
        <div className="flex flex-wrap gap-4">
          <TextField
            className="flex-1 flex-grow-[2] min-w-[200px]"
            name={`${name}.city`}
            label="City"
            variant={variant}
            required={required}
            value={watch(`${name}.city`)}
            onChangeText={(value) => setAddressField("city", value)}
            disabled={isSubmitting}
            error={!!getAddressFieldError(errors, name, "city")}
            errorText={getAddressFieldError(errors, name, "city")?.message}
          />
          <div className="flex-1 min-w-[100px]">
            <StateInput control={control} name={`${name}.state` as `${string}.state`} isUSA={isUSA} required={isUSA} />
          </div>
          <PostalCodeInput
            className="flex-1 min-w-[100px]"
            control={control}
            name={`${name}.postalCode` as `${string}.postalCode`}
            isUSA={isUSA}
            required={isUSA}
            label="Postal Code"
          />
        </div>
      </div>
    </div>
  );
};

export default AddressInput;
